import { getChainNameFromDomainId } from "../domains";

/**
 * Fields of a CCTP v2 MessageV2 carrying a BurnMessageV2 body.
 *
 * The nonce is assigned by Circle's attesters and is all zeros in the message emitted on the
 * source chain, so only an attested message (from Circle's API) carries the real one.
 */
export interface ParsedCCTPMessageV2 {
  sourceDomain: number;
  sourceChain: string | null;
  destDomain: number;
  nonce: string;
  sender: string;
  recipient: string;
  destinationCaller: string;
  minFinalityThreshold: number;
  finalityThresholdExecuted: number;
  burnToken: string;
  mintRecipient: string;
  /** Burned amount in USDC subunits. The recipient receives `amount - feeExecuted`. */
  amount: bigint;
  messageSender: string;
  maxFee: bigint;
  feeExecuted: bigint;
  expirationBlock: bigint;
  hookData: string;
}

const MESSAGE_VERSION_V2 = 1;
const BURN_MESSAGE_VERSION_V2 = 1;
const HEADER_BYTES = 148;
const BURN_BODY_MIN_BYTES = 228;
const HEX_RE = /^(?:[0-9a-fA-F]{2})*$/;

export function parseCCTPMessageV2(messageHex: string): ParsedCCTPMessageV2 | null {
  const hex = (messageHex.startsWith("0x") ? messageHex.slice(2) : messageHex).toLowerCase();
  if (!HEX_RE.test(hex) || hex.length < (HEADER_BYTES + BURN_BODY_MIN_BYTES) * 2) return null;

  const u32 = (offset: number) => parseInt(hex.slice(offset * 2, offset * 2 + 8), 16);
  const bytes32 = (offset: number) => `0x${hex.slice(offset * 2, offset * 2 + 64)}`;
  const u256 = (offset: number) => BigInt(bytes32(offset));

  const body = HEADER_BYTES;
  // Any other body version isn't a BurnMessageV2, so the offsets below wouldn't apply.
  if (u32(0) !== MESSAGE_VERSION_V2 || u32(body) !== BURN_MESSAGE_VERSION_V2) return null;

  const sourceDomain = u32(4);

  return {
    sourceDomain,
    sourceChain: getChainNameFromDomainId(sourceDomain),
    destDomain: u32(8),
    nonce: bytes32(12),
    sender: bytes32(44),
    recipient: bytes32(76),
    destinationCaller: bytes32(108),
    minFinalityThreshold: u32(140),
    finalityThresholdExecuted: u32(144),
    burnToken: bytes32(body + 4),
    mintRecipient: bytes32(body + 36),
    amount: u256(body + 68),
    messageSender: bytes32(body + 100),
    maxFee: u256(body + 132),
    feeExecuted: u256(body + 164),
    expirationBlock: u256(body + 196),
    hookData: `0x${hex.slice((body + BURN_BODY_MIN_BYTES) * 2)}`,
  };
}
